import styled from '@emotion/styled';
import { SubscriptionPlan } from '../../redux/slices/subscriptionSlice';

interface PlanCardProps {
  plan: SubscriptionPlan;
  isCurrentPlan: boolean;
  onSelect: (plan: SubscriptionPlan) => void;
}

const PlanCard = ({ plan, isCurrentPlan, onSelect }: PlanCardProps) => {
  const isFree = plan.code === 'FREE';
  const featured = plan.code.endsWith('PLUS');
  const featuredColor = plan.code.startsWith('RECRUITER') ? '#7540ed' : '#284b9f';
  const features = [
    plan.jobApplicationLimit === 0 ? "Can't apply to jobs" : plan.jobApplicationLimit >= 999 ? 'Unlimited job applications' : `${plan.jobApplicationLimit} job applications/month`,
    plan.jobPostLimit === 0 ? "Can't post a job" : plan.jobPostLimit >= 999 ? 'Unlimited job posts' : `${plan.jobPostLimit} job posts/month`,
    plan.jobAlertAccess ? 'Job alerts' : 'No job alerts',
    plan.jobInviteAccess ? 'Job invites' : 'No job invites',
    plan.findLocumAccess ? 'Access to find a locum' : "Can't find a locum",
  ];

  return (
    <Card featured={featured} featuredColor={featuredColor}>
      <Name>{plan.name}</Name>
      <Price>{plan.currency}{Number(plan.amount).toLocaleString('en-NG', { maximumFractionDigits: 0 })}</Price>
      <Period>Per {plan.billingInterval === 'ANNUAL' && !isFree ? 'Year' : 'Month'}</Period>
      <Divider />
      <Included>What's included?</Included>
      <Features>
        {features.map((feature) => <li key={feature}><Check featured={featured} featuredColor={featuredColor} aria-hidden="true">✓</Check><span>{feature}</span></li>)}
      </Features>
      <Action type="button" featured={featured} featuredColor={featuredColor} disabled={isFree || isCurrentPlan} onClick={() => onSelect(plan)}>
        {isCurrentPlan ? 'Current Plan' : isFree ? 'Free Plan' : 'Get Started'}
      </Action>
    </Card>
  );
};

export default PlanCard;

const Card = styled.article<{ featured: boolean; featuredColor: string }>`
  display: flex;
  flex-direction: column;
  min-height: 580px;
  border: 1px solid ${({ featured, featuredColor }) => featured ? featuredColor : '#315bd0'};
  border-radius: 12px;
  background: ${({ featured, featuredColor }) => featured ? featuredColor : '#fff'};
  color: ${({ featured }) => featured ? '#fff' : '#121722'};
  padding: clamp(22px, 2vw, 38px);
`;

const Name = styled.h2`
  margin: 0 0 12px;
  font-size: clamp(18px, 1.5vw, 24px);
  font-weight: 700;
  text-transform: uppercase;
`;

const Price = styled.strong`
  font-size: clamp(30px, 2.8vw, 50px);
  line-height: 1.1;
  white-space: nowrap;
`;

const Period = styled.span`
  margin-top: 8px;
  font-size: 17px;
  opacity: 0.75;
`;

const Divider = styled.hr`
  width: 100%;
  border: 0;
  border-top: 1px solid currentColor;
  margin: 28px 0;
  opacity: 0.5;
`;

const Included = styled.h3`
  margin: 0 0 20px;
  font-size: 17px;
`;

const Features = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin: 0 0 40px;
  padding: 0;
  list-style: none;
  flex: 1;
  li { display: flex; align-items: flex-start; gap: 13px; font-size: 17px; line-height: 1.4; }
`;

const Check = styled.span<{ featured: boolean; featuredColor: string }>`
  display: inline-grid;
  place-items: center;
  width: 23px;
  height: 23px;
  flex: none;
  border-radius: 50%;
  background: ${({ featured }) => featured ? '#fff' : '#243044'};
  color: ${({ featured, featuredColor }) => featured ? featuredColor : '#fff'};
  font-size: 15px;
`;

const Action = styled.button<{ featured: boolean; featuredColor: string }>`
  width: 100%;
  border: 1px solid ${({ featuredColor }) => featuredColor};
  border-radius: 999px;
  padding: 14px 20px;
  background: ${({ featured }) => featured ? '#fff' : 'transparent'};
  color: ${({ featuredColor }) => featuredColor};
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  &:disabled { cursor: default; opacity: 0.7; }
  &:focus-visible { outline: 3px solid #8657ff; outline-offset: 3px; }
`;
